// WizardStepModal — overlay popup used by the customer-facing
// EditorWizardBar (Designer + DTF) to focus a basic user on the inputs
// for one step at a time. Generic chrome only; the host supplies the
// step-specific JSX as children.
//
// Brand: brick header on cream body so it matches the wizard bar that
// triggered it. ESC closes; backdrop click closes.
import { useEffect } from 'react'

export default function WizardStepModal({
  open, title, subtitle, stepNumber, totalSteps,
  onClose, onPrev, onNext, nextLabel, prevLabel = '← Back', children,
  nextDisabled = false,
}: {
  open: boolean
  /** Short step title — e.g. "Garment + colour". */
  title: string
  /** One-line plain-English explainer above the inputs. */
  subtitle?: string
  /** 1-indexed step number, shown in the header chip. */
  stepNumber: number
  totalSteps: number
  onClose: () => void
  /** When set, shows a Back button that calls this. */
  onPrev?: () => void
  /**
   * When set, shows a primary Next button that calls this. Common
   * pattern: setStep(nextId) so the modal flips to the next step
   * without closing. Pass undefined to omit the button.
   */
  onNext?: () => void
  /** Defaults to "Next →". Override for the final step ("Submit"). */
  nextLabel?: string
  prevLabel?: string
  /** Disable Next when the step's data isn't filled yet. */
  nextDisabled?: boolean
  children: React.ReactNode
}) {
  // ESC closes — common modal convention.
  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  if (!open) return null

  return (
    <div
      className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4 animate-in fade-in"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-cream rounded-lg shadow-2xl max-w-xl w-full max-h-[88vh] flex flex-col overflow-hidden border border-brick/40">

        {/* Header — brick band with step chip + title. Mirrors the wizard
            bar so users see the same visual language they just clicked. */}
        <div className="bg-brick text-white px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full bg-white text-brick text-xs font-bold">
              {stepNumber}
            </span>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-white/70">
                Step {stepNumber} of {totalSteps}
              </div>
              <div className="text-sm font-semibold truncate">{title}</div>
            </div>
          </div>
          <button onClick={onClose}
            className="w-8 h-8 rounded hover:bg-white/15 flex items-center justify-center"
            aria-label="Close">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.4} strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        {/* Optional subtitle row */}
        {subtitle && (
          <div className="px-5 pt-4 pb-1 text-sm text-ink-2 shrink-0">{subtitle}</div>
        )}

        {/* Step content — scrolls if the inputs run long. */}
        <div className="px-5 py-4 flex-1 overflow-y-auto">{children}</div>

        {/* Footer — Back / Skip / Next */}
        <div className="border-t border-brick/20 bg-cream/80 px-5 py-3 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2">
            {onPrev && (
              <button type="button" onClick={onPrev}
                className="px-3 py-1.5 rounded border border-brick/40 text-brick text-xs font-semibold uppercase tracking-wide hover:bg-brick-tint">
                {prevLabel}
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1.5 rounded text-ink-3 text-xs font-semibold uppercase tracking-wide hover:text-ink hover:bg-white">
              Skip for now
            </button>
            {onNext && (
              <button type="button" onClick={onNext} disabled={nextDisabled}
                className="px-4 py-1.5 rounded bg-brick text-white text-xs font-semibold uppercase tracking-wide hover:bg-brick-dark disabled:opacity-40 disabled:cursor-not-allowed">
                {nextLabel ?? 'Next →'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
